import React, { useEffect, useReducer, useState } from 'react';
import { useConfig } from '../../../DataStore/ConfigContext';
import Notification from '../../Common/Notification';
import CallOut from '../../Common/CallOut';
import TextInput from '../../Common/TextInput';
import ajaxConfig from '../../../util/remote/ajaxConfig';
import { useTheme } from '../../../DataStore/ThemeContext';

/* ---------------- helpers ---------------- */
const buildStateFromPreference = (preference) => ({
  id:               preference?.id ?? '',
  preference_key:   preference?.preference_key ?? '',
  preference_name:  preference?.preference_name ?? '',
  preference_value: preference?.preference_value ?? '',
  loading:          false,
});

const isBooleanLike = (v) => {
  const s = String(v ?? '').trim().toUpperCase();
  return s === 'YES' || s === 'NO' || s === 'TRUE' || s === 'FALSE' || s === 'ON' || s === 'OFF' || s === '1' || s === '0';
};
const normalizeBooleanLike = (v) => {
  const s = String(v ?? '').trim().toUpperCase();
  if (['YES','TRUE','ON','1'].includes(s)) return 'YES';
  if (['NO','FALSE','OFF','0'].includes(s)) return 'NO';
  return s || '';
};

/* ---------------- reducer ---------------- */
const reducer = (state, action) => {
  switch (action.type) {
    case 'RESET':
      return buildStateFromPreference(action.payload || {});
    case 'SET_ALL':
      return { ...state, ...action.value };
    default:
      return { ...state, [action.type]: action.value };
  }
};

/* ===================== EditPreference ===================== */
const EditPreference = ({ preference }) => {
  // initialize from props on first render
  const [state, dispatch] = useReducer(reducer, preference, buildStateFromPreference);
  const [notify, setNotify] = useState(null);
  const { getPreferences } = useConfig();
  const {setSystemFunction} = useTheme();

  // keep in sync if `preference` prop changes later
  useEffect(() => {
    dispatch({ type: 'SET_ALL', value: buildStateFromPreference(preference) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preference?.id]);

  useEffect(()=>{
      setSystemFunction(() => handleSave);
      return () => setSystemFunction(() => {});
    }, [state]);

  const validate = () => {
    if (!state.preference_value?.toString().trim()) return 'Preference value is required.';
    return null;
  };


  const onChangeText = (e) => {
    const { name, value } = e.target;
    dispatch({ type: name, value });
  };

  const RenderValueField = () => {
    // If the current value is boolean-like, render a select; otherwise text input
    const isBool = isBooleanLike(state.preference_value);

    if (isBool) {
      const options = [
        { label: 'Yes', value: 'YES' },
        { label: 'No',  value: 'NO'  },
      ];
      const current = options.find(o => o.value === normalizeBooleanLike(state.preference_value)) || options[1];
      return (
        <TextInput
          label="Value"
          type="select"
          name="preference_value"
          leftIcon="fa fa-toggle-on"
          value={current}
          options={options}
          onChange={(opt) => dispatch({ type: 'preference_value', value: opt.value })}
          wrap
        />
      );
    }

    // For currency and other free-text values
    return (
      <TextInput
        type="text"
        label="Value"
        placeholder="Enter value"
        leftIcon="fa fa-pen"
        value={state.preference_value}
        name="preference_value"
        onChange={onChangeText}
        required
        wrap
      />
    );
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      setNotify(<Notification type="danger" message={error} />);
      return;
    }

    dispatch({ type: 'loading', value: true });

    const payload = {
    id: state.id,
    preference_key: state.preference_key,
    preference_value: state.preference_value,
    };

    // Support either naming convention in your ajax layer
    const res = await ajaxConfig.updatePreference(payload.id, payload.preference_value);
    dispatch({ type: 'loading', value: false });
    if (res.status === 'OK') {
    setNotify(<Notification type="success" message={res.message || 'Preference updated.'} />);
    getPreferences?.(true);
    } else {
    setNotify(<Notification type="danger" message={res?.message || 'Failed to update preference.'} />);
    }
    
  };

  return (
    <>
      <CallOut
        title="Edit Preference"
        description="Update how your workspace behaves. Only the value is editable; the name and key are shown for reference."
      />
      <div className="mt-4">{notify}</div>


      <form className="form-horizontal" onSubmit={handleSave}>
        {/* Read-only reference fields */}
        <div className="row">
          <div className="col-md-6">
            <TextInput
              type="text"
              label="Preference Name"
              leftIcon="fa fa-sliders-h"
              value={state.preference_name}
              name="preference_name"
              readOnly
              wrap
            />
          </div>
          <div className="col-md-6">
            <TextInput
              type="text"
              label="Preference Key"
              leftIcon="fa fa-key"
              value={state.preference_key}
              name="preference_key"
              readOnly
              wrap
            />
          </div>
        </div>

        {/* Editable value */}
        {RenderValueField()}
      </form>
    </>
  );
};

export default EditPreference;
